import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../Common/isRequestCanceled";
import {
  BASE_NODE_URL,
  BASE_URL,
  LIST_ALL_STORE_ORDER_LIST,
  LIST_ALL_STORE_ORDER_LIST_NEW,
  UPDATE_ORDER_STATUS,
} from "../../../Constants/Config";

const initialState = {
  loading: false,
  onlineStoreOrderData: [],
  OrderChangeStatusData: [],
  OrderListCount: 0,
  allOrdersCount: 0,
  successMessage: "",
  error: "",
  latestRequestId: null, // Store the latest request ID
  uberLoading: false,
  uberData: [],
  uberError: false,

  deliveryLoading: false,

  subOrderStatusCounts: {
    being_processed_count: 0,
    out_for_delivery_count: 0,
    ready_for_pickup_count: 0,
  },
};

let abortController = null;
let OnlineExportAbortController = null;

const pickNumericCount = (...candidates) => {
  for (const v of candidates) {
    if (v == null || v === "") continue;
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
};

/** Fetches online order_list for Excel export only — does not update Redux listing state. */
export const fetchOnlineOrderListForExport = async (data) => {
  if (OnlineExportAbortController) {
    OnlineExportAbortController.abort();
  }
  OnlineExportAbortController = new AbortController();
  const signal = OnlineExportAbortController.signal;

  const { token, ...newData } = data;
  const response = await axios.post(
    BASE_NODE_URL + LIST_ALL_STORE_ORDER_LIST_NEW,
    newData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
      },
      signal,
    }
  );
  if (response.data.status !== true) {
    throw new Error(response.data?.message ?? "Export request failed");
  }
  const orderData = response.data.order_data;
  return Array.isArray(orderData) ? orderData : orderData?.list ?? [];
};

/** Totals for online status sub-tabs; read from list API (`being_processed_count`, etc.). */
function parseSubOrderStatusCounts(apiRoot) {
  const defaults = {
    being_processed_count: 0,
    out_for_delivery_count: 0,
    ready_for_pickup_count: 0,
  };
  if (!apiRoot || typeof apiRoot !== "object") return defaults;

  const pickNum = (...candidates) => {
    for (const v of candidates) {
      if (v == null || v === "") continue;
      const n = Number(v);
      if (!Number.isNaN(n)) return n;
    }
    return null;
  };

  const orderData = apiRoot.order_data;
  const nested =
    orderData && typeof orderData === "object" && !Array.isArray(orderData) ? orderData : null;

  const being = pickNum(
    apiRoot.being_processed_count,
    nested?.being_processed_count,
    apiRoot.beingProcessedCount,
    nested?.beingProcessedCount
  );
  const outDel = pickNum(
    apiRoot.out_for_delivery_count,
    nested?.out_for_delivery_count,
    apiRoot.outForDeliveryCount,
    nested?.outForDeliveryCount
  );
  const readyPu = pickNum(
    apiRoot.ready_for_pickup_count,
    nested?.ready_for_pickup_count,
    apiRoot.readyForPickupCount,
    nested?.readyForPickupCount
  );

  return {
    being_processed_count: being ?? 0,
    out_for_delivery_count: outDel ?? 0,
    ready_for_pickup_count: readyPu ?? 0,
  };
}

// Generate pening , fulfilled and rejected action type
export const fetchOnlineStoreOrderData = createAsyncThunk(
  "onlineStoreOrder/fetchOnlineStoreOrderData",
  async (data, { rejectWithValue, requestId }) => {
    if (abortController) {
      abortController.abort(); // Cancel the previous request
    }

    abortController = new AbortController();
    const signal = abortController.signal;

    const { token, ...newData } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + LIST_ALL_STORE_ORDER_LIST_NEW,
        newData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal, // Pass the signal for cancellation
        }
      );

      if (response.data.status === true) {
        const orderData = response.data.order_data;
        const normalized = Array.isArray(orderData) ? orderData : orderData?.list ?? [];
        const nested =
          orderData && typeof orderData === "object" && !Array.isArray(orderData)
            ? orderData
            : null;
        const totalCount = pickNumericCount(
          response.data.all_orders_count,
          response.data.all_order_count,
          response.data.allOrdersCount,
          nested?.all_orders_count,
          nested?.total_count,
          nested?.total
        );
        const subOrderStatusCounts = parseSubOrderStatusCounts(response.data);
        return { requestId, data: normalized, totalCount, subOrderStatusCounts };
      }
    } catch (error) {
      if (error.name === "AbortError") {
        return rejectWithValue({ message: "Request canceled", requestId });
      }

      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
        requestId,
      };
      return rejectWithValue(customError);
    }
  }
);

export const fetchOrderChangeStatusData = createAsyncThunk(
  "onlineStoreOrder/fetchOrderChangeStatusData.",
  async (data) => {
    const { token, ...newData } = data;
    try {
      const response = await axios.post(
        BASE_URL + UPDATE_ORDER_STATUS,
        newData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.status === true) {
        return response.data;
      }
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  }
);

export const getOrderListCount = createAsyncThunk(
  "onlineStoreOrder/getOrderListCount",
  async (data) => {
    const { token, ...dataNew } = data;
    const response = await axios.post(
      BASE_URL + "Order_list_api/all_order_list_count",
      dataNew,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`, // Use data?.token directly
        },
      }
    );
    // console.log("AllInStoreDataState OrderListCount: ", response.data.order_data);
    if (response.status == 200) {
      return response.data.order_data;
    }
  }
);

export const calledUberDirectAPI = createAsyncThunk(
  "onlineStoreOrder/calleUberDirectAPI",
  async (data) => {
    const { token, ...dataNew } = data;
    const response = await axios.post(
      BASE_URL + "Order_list_api/get_delivery_status",
      dataNew,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`, // Use data?.token directly
        },
      }
    );
    return response;
  }
);

// export const UberOrderList = createAsyncThunk(
//   "onlineStoreOrder/UberOrderList",
//   async (data) => {
//     const { token, ...dataNew } = data;
//     const response = await axios.post(
//       BASE_URL + "Order_list_api/uber_order_list",
//       dataNew,
//       {
//         headers: {
//           "Content-Type": "multipart/form-data",
//           Authorization: `Bearer ${token}`, // Use data?.token directly
//         },
//       }
//     );
//     return response?.data?.result;
//   }
// );

export const getDeliveryStatus = createAsyncThunk(
  "onlineStoreOrder/getDeliveryStatus",
  async (data) => {
    const { token, ...dataNew } = data;

    const response = await axios.post(
      BASE_URL + "Order_list_api/get_delivery_status",
      dataNew,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`, // Use data?.token directly
        },
      }
    );
    return response;
  }
);

export const getDeliveryCancelStatus = createAsyncThunk(
  "onlineStoreOrder/getDeliveryCancelStatus",
  async (data) => {
    const { token, ...dataNew } = data;
    const response = await axios.post(
      BASE_URL + "api/Uber_webhooks/cancel_delivery",
      dataNew,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response;
  }
);

// ========== NEW UBER DIRECT APIs (QV-6039) — ACTIVE ==========
// Comment out this entire block when switching to PREVIOUS Uber Direct UI.
// PREVIOUS UI only uses: calledUberDirectAPI, getDeliveryStatus, getDeliveryCancelStatus

// QV-6039: Driver contact gateway — returns courier info while delivery is active.
export const fetchDriverContact = createAsyncThunk(
  "onlineStoreOrder/fetchDriverContact",
  async (data) => {
    const { token, ...dataNew } = data;
    const response = await axios.post(
      BASE_URL + "api/Uber_webhooks/get_driver_contact",
      dataNew,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response;
  }
);

// QV-6039: Resolution protocol for failed/cancelled deliveries.
export const fetchResolutionProtocol = createAsyncThunk(
  "onlineStoreOrder/fetchResolutionProtocol",
  async (data) => {
    const { token, ...dataNew } = data;
    const response = await axios.post(
      BASE_URL + "api/Uber_webhooks/get_resolution_protocol",
      dataNew,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response;
  }
);

// QV-6039: Support escalation payload — tracking ID + Quickvee Support contact.
export const fetchSupportEscalationPayload = createAsyncThunk(
  "onlineStoreOrder/fetchSupportEscalationPayload",
  async (data) => {
    const { token, ...dataNew } = data;
    const response = await axios.post(
      BASE_URL + "api/Uber_webhooks/support_escalation_payload",
      dataNew,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response;
  }
);
// ========== END NEW UBER DIRECT APIs (QV-6039) ==========

const onlineStoreOrderSlice = createSlice({
  name: "onlineStoreOrder",
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(fetchOnlineStoreOrderData.pending, (state, action) => {
        state.loading = true;
        state.latestRequestId = action.meta.requestId; // Store latest request ID
      })
      .addCase(fetchOnlineStoreOrderData.fulfilled, (state, action) => {
        if (state.latestRequestId !== action.meta.requestId) {
          return; // Ignore outdated responses
        }
        state.loading = false;
        state.onlineStoreOrderData = action.payload?.data ?? [];
        if (action.payload?.totalCount != null) {
          state.allOrdersCount = action.payload.totalCount;
          state.OrderListCount = action.payload.totalCount;
        }
        if (action.payload?.subOrderStatusCounts) {
          state.subOrderStatusCounts = action.payload.subOrderStatusCounts;
        }
        state.error = null;
      })
      .addCase(fetchOnlineStoreOrderData.rejected, (state, action) => {
        if (state.latestRequestId !== action.meta.requestId) {
          return; // Ignore outdated errors
        }

        if (action.payload?.message === "Request canceled") {
          return; // Do nothing if canceled, keep loading true
        }

        state.loading = false;
        state.error = action.payload?.message || "Something went wrong";
      });
    // for Chnage order status
    builder.addCase(fetchOrderChangeStatusData.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchOrderChangeStatusData.fulfilled, (state, action) => {
      state.loading = false;
      state.OrderChangeStatusData = action.payload;
      state.error = "";
    });
    builder.addCase(fetchOrderChangeStatusData.rejected, (state, action) => {
      state.loading = false;
      state.OrderChangeStatusData = {};
      state.error = action.error.message;
    });

    /// UBER DIRECT API CALLED
    builder.addCase(calledUberDirectAPI.pending, (state) => {
      state.uberLoading = true;
    });
    builder.addCase(calledUberDirectAPI.fulfilled, (state, action) => {
      state.uberLoading = false;
      state.uberData = action.payload;
      state.uberError = "";
    });
    builder.addCase(calledUberDirectAPI.rejected, (state, action) => {
      state.uberLoading = false;
      state.uberData = {};
      state.uberError = action.error.message;
    });

    /// UBER DIRECT DELIVERY STATUS API CALLED
    builder.addCase(getDeliveryStatus.pending, (state) => {
      state.deliveryLoading = true;
    });
    builder.addCase(getDeliveryStatus.fulfilled, (state, action) => {
      state.deliveryLoading = false;
    });
    builder.addCase(getDeliveryStatus.rejected, (state, action) => {
      state.deliveryLoading = false;
    });


    /// UBER DIRECT DELIVERY Cancel STATUS API CALLED
    builder.addCase(getDeliveryCancelStatus.pending, (state) => {
      state.deliveryLoading = true;
    });
    builder.addCase(getDeliveryCancelStatus.fulfilled, (state, action) => {
      state.deliveryLoading = false;
    });
    builder.addCase(getDeliveryCancelStatus.rejected, (state, action) => {
      state.deliveryLoading = false;
    });

    builder.addCase(getOrderListCount.fulfilled, (state, action) => {
      state.OrderListCount = action.payload;
    });
    builder.addCase(getOrderListCount.rejected, (state, action) => {
      state.OrderListCount = 0;
    });
  },
});

export default onlineStoreOrderSlice.reducer;
